import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../styles/ThemeContext';

const STANDARD_VIEWBOX = "0 0 243 74";
const STANDARD_PATH = "M23.1473 58.5227L12.4152 40.3048C12.1434 39.8433 12 39.3176 12 38.782V15C12 13.3431 13.3431 12 15 12H215.268C216.33 12 217.313 12.5619 217.853 13.4773L228.585 31.6952C228.857 32.1567 229 32.6824 229 33.218V57C229 58.6569 227.657 60 226 60H25.7321C24.6697 60 23.6865 59.4381 23.1473 58.5227Z";
const WIDE_VIEWBOX = "0 0 450 74";
const WIDE_PATH = "M23.1473 58.5227L12.4152 40.3048C12.1434 39.8433 12 39.3176 12 38.782V15C12 13.3431 13.3431 12 15 12H422.268C423.33 12 424.313 12.5619 424.853 13.4773L435.585 31.6952C435.857 32.1567 436 32.6824 436 33.218V57C436 58.6569 434.657 60 433 60H25.7321C24.6697 60 23.6865 59.4381 23.1473 58.5227Z";

const SidebarRoleButton = ({ text = 'User', wide = false, collapsed = false, fullText }) => {
  const { theme, isDarkMode } = useTheme();
  const [tooltipPos, setTooltipPos] = useState(null);
  const circleRef = useRef(null);

  const showTooltip = useCallback(() => {
    if (!circleRef.current) return;
    const rect = circleRef.current.getBoundingClientRect();
    setTooltipPos({ top: rect.top + rect.height / 2, left: rect.right + 12 });
  }, []);

  const hideTooltip = useCallback(() => setTooltipPos(null), []);

  if (collapsed) {
    return (
      <div
        className="sidebar-role-circle"
        style={{ background: theme.primary }}
        ref={circleRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        <span>{text}</span>
        {tooltipPos && createPortal(
          <span className="menu-tooltip" style={{ top: tooltipPos.top, left: tooltipPos.left }}>
            {fullText || text}
          </span>,
          document.body
        )}
      </div>
    );
  }

  const viewBox = wide ? WIDE_VIEWBOX : STANDARD_VIEWBOX;
  const shapePath = wide ? WIDE_PATH : STANDARD_PATH;
  const filterWidth = wide ? '450' : '243';
  const filterId = wide ? 'srb_filter_wide' : 'srb_filter_std';
  const gradientId = wide ? 'srb_grad_wide' : 'srb_grad_std';

  const shadowColors = isDarkMode ? {
    light: { r: 0.196, g: 0.196, b: 0.196 },
    mid:   { r: 0.039, g: 0.039, b: 0.039 },
    dark:  { r: 0.078, g: 0.078, b: 0.078 },
  } : {
    light: { r: 1.0,   g: 1.0,   b: 1.0   },
    mid:   { r: 0.812, g: 0.812, b: 0.812 },
    dark:  { r: 0.812, g: 0.812, b: 0.812 },
  };

  // In dark mode: gradient from primary colour → dark bg. In light mode: solid primary.
  const darkBg = '#1E1E1E';

  return (
    <div style={{ width: '100%', pointerEvents: 'none' }}>
      <svg
        width="100%"
        viewBox={viewBox}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
        overflow="visible"
        style={{ pointerEvents: 'none' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            {isDarkMode ? (
              <>
                <stop offset="0%" stopColor={theme.primary} />
                <stop offset="100%" stopColor={darkBg} />
              </>
            ) : (
              <>
                <stop offset="0%" stopColor={theme.primary} />
                <stop offset="100%" stopColor={theme.primary} />
              </>
            )}
          </linearGradient>

          <filter id={filterId} x="0" y="0" width={filterWidth} height="74" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="4" dy="4"/>
            <feGaussianBlur stdDeviation="5"/>
            <feColorMatrix type="matrix" values={`0 0 0 0 ${shadowColors.dark.r} 0 0 0 0 ${shadowColors.dark.g} 0 0 0 0 ${shadowColors.dark.b} 0 0 0 0.9 0`}/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-4" dy="-4"/>
            <feGaussianBlur stdDeviation="4"/>
            <feColorMatrix type="matrix" values={`0 0 0 0 ${shadowColors.light.r} 0 0 0 0 ${shadowColors.light.g} 0 0 0 0 ${shadowColors.light.b} 0 0 0 0.9 0`}/>
            <feBlend mode="normal" in2="effect1_dropShadow" result="effect2_dropShadow"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="4" dy="-4"/>
            <feGaussianBlur stdDeviation="4"/>
            <feColorMatrix type="matrix" values={`0 0 0 0 ${shadowColors.dark.r} 0 0 0 0 ${shadowColors.dark.g} 0 0 0 0 ${shadowColors.dark.b} 0 0 0 0.2 0`}/>
            <feBlend mode="normal" in2="effect2_dropShadow" result="effect3_dropShadow"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-4" dy="4"/>
            <feGaussianBlur stdDeviation="4"/>
            <feColorMatrix type="matrix" values={`0 0 0 0 ${shadowColors.dark.r} 0 0 0 0 ${shadowColors.dark.g} 0 0 0 0 ${shadowColors.dark.b} 0 0 0 0.2 0`}/>
            <feBlend mode="normal" in2="effect3_dropShadow" result="effect4_dropShadow"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect4_dropShadow" result="shape"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-1" dy="-1"/>
            <feGaussianBlur stdDeviation="1"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values={`0 0 0 0 ${shadowColors.dark.r} 0 0 0 0 ${shadowColors.dark.g} 0 0 0 0 ${shadowColors.dark.b} 0 0 0 0.5 0`}/>
            <feBlend mode="normal" in2="shape" result="effect5_innerShadow"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="1" dy="1"/>
            <feGaussianBlur stdDeviation="1"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values={`0 0 0 0 ${shadowColors.light.r} 0 0 0 0 ${shadowColors.light.g} 0 0 0 0 ${shadowColors.light.b} 0 0 0 0.3 0`}/>
            <feBlend mode="normal" in2="effect5_innerShadow" result="effect6_innerShadow"/>
          </filter>
        </defs>

        <g filter={`url(#${filterId})`}>
          <path
            d={shapePath}
            fill={`url(#${gradientId})`}
            style={{ transition: 'fill 0.3s ease' }}
          />
        </g>

        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="white"
          fontSize="20"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontWeight="500"
        >
          {text}
        </text>
      </svg>
    </div>
  );
};

export default SidebarRoleButton;
